import type { LayerInfo } from '@/lib/api';

interface LayerPanelProps {
  layers: LayerInfo[];
  active: ReadonlySet<string>;
  onToggle: (key: string) => void;
  loading: ReadonlySet<string>;
}

/**
 * Layer toggles. Every layer is off by default, and every layer shows its
 * source and its data vintage next to the switch — a layer whose age is
 * invisible invites the reader to assume it is current.
 */
export function LayerPanel({ layers, active, onToggle, loading }: LayerPanelProps) {
  if (layers.length === 0) return null;

  return (
    // Collapsed by default. Every layer is off by default anyway, so an
    // always-expanded panel is a control nobody is using that permanently
    // covers part of the map — worst on a phone, where it took a quarter of it.
    <details className="rounded-lg border border-slate-200 bg-white/95 shadow-lg backdrop-blur">
      <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-slate-900">
        Datové vrstvy
        {active.size > 0 && <span className="ml-1 font-normal text-slate-600">({active.size})</span>}
      </summary>
      <ul className="space-y-2 px-3 pb-3">
        {layers.map((layer) => (
          <li key={layer.key}>
            <label className="flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                checked={active.has(layer.key)}
                onChange={() => onToggle(layer.key)}
                className="mt-0.5 h-4 w-4 accent-brand-700"
              />
              <span className="text-sm">
                <span className="font-medium text-slate-900">{layer.label}</span>
                {loading.has(layer.key) && (
                  <span className="ml-1 text-slate-500" role="status">
                    načítám…
                  </span>
                )}
                <span className="block text-xs text-slate-600">{layer.attribution}</span>
                <span className="block text-xs text-slate-500">Data: {layer.vintage}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </details>
  );
}
